import React, { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useWebSocketMessages, WsMessage } from '@shared/hooks/useWebSocketMessages';
import { IProcessedMeasurement, IProcessedMeasurementError, TMeasurementValue } from 'app/types/measurement';
import { processMessage, processErrorMessage, formatValue } from 'app/services/WebSocketMeasurementService';

const MAX_DEDUP_SIZE = 200;
const MAX_MEASUREMENTS = 50;

interface WebSocketMeasurementContextValue {
  measurements: IProcessedMeasurement[];
  measurementErrors: IProcessedMeasurementError[];
  rawMessages: WsMessage[];
  status: string;
  connect: () => void;
  disconnect: () => void;
  isConnected: boolean;
  formatValue: (value: TMeasurementValue, type: string) => string;
}

const WebSocketMeasurementContext = createContext<WebSocketMeasurementContextValue | null>(null);

interface WebSocketMeasurementProviderProps {
  children: ReactNode;
  url: string;
}

export const WebSocketMeasurementProvider: React.FC<WebSocketMeasurementProviderProps> = ({
  children,
  url,
}) => {
  const { status, messages, connect, disconnect } = useWebSocketMessages(url);
  const [measurements, setMeasurements] = useState<IProcessedMeasurement[]>([]);
  const [measurementErrors, setMeasurementErrors] = useState<IProcessedMeasurementError[]>([]);
  const processedIdsRef = useRef(new Set<string>());

  // Old pathway data must not bleed into the new session.
  useEffect(() => {
    setMeasurements([]);
    setMeasurementErrors([]);
    processedIdsRef.current.clear();
  }, [url]);

  useEffect(() => {
    if (messages.length === 0) return;

    const dedupKeyOf = (message: WsMessage) => ((message.json as any)?.measurement?.id as string | undefined) ?? message.id;

    const unprocessed = messages
      .filter(message => message.direction === 'in' && message.json && !processedIdsRef.current.has(dedupKeyOf(message)))
      .reverse();

    if (unprocessed.length === 0) return;

    const newErrors: IProcessedMeasurementError[] = [];
    const newMeasurements: IProcessedMeasurement[] = [];

    unprocessed.forEach(message => {
      const dedupKey = dedupKeyOf(message);
      if (processedIdsRef.current.size >= MAX_DEDUP_SIZE) {
        const oldest = processedIdsRef.current.values().next().value;
        if (oldest !== undefined) processedIdsRef.current.delete(oldest);
      }
      processedIdsRef.current.add(dedupKey);

      const processedError = processErrorMessage(message.json);
      if (processedError) {
        newErrors.push(processedError);
        return;
      }

      const processed = processMessage(message.json);
      if (processed) newMeasurements.push(processed);
    });

    if (newErrors.length > 0) {
      setMeasurementErrors(prev => [...newErrors.reverse(), ...prev].slice(0, MAX_MEASUREMENTS));
    }
    if (newMeasurements.length > 0) {
      setMeasurements(prev => [...newMeasurements.reverse(), ...prev].slice(0, MAX_MEASUREMENTS));
    }
  }, [messages]);

  const contextValue = useMemo<WebSocketMeasurementContextValue>(() => ({
    measurements,
    measurementErrors,
    rawMessages: messages,
    status,
    connect,
    disconnect,
    isConnected: status === 'open',
    formatValue,
  }), [measurements, measurementErrors, messages, status, connect, disconnect]);

  return (
    <WebSocketMeasurementContext.Provider value={contextValue}>
      {children}
    </WebSocketMeasurementContext.Provider>
  );
};

export const useWebSocketMeasurementContext = (): WebSocketMeasurementContextValue => {
  const context = useContext(WebSocketMeasurementContext);
  if (!context) {
    throw new Error('useWebSocketMeasurementContext must be used within a WebSocketMeasurementProvider');
  }
  return context;
};
